import { useEffect, useMemo } from 'react';

import {
  SelectOption,
  useFormContext,
  useWatch,
} from '@kanda-libs/ks-component-ts';

import { useCurrentJob, useCurrentJobLead } from 'hooks';
import usePreviousValue from 'hooks/usePreviousValue';
import { FIELD_NAMES } from 'pages/Quote/constants';
import { someRatesAreBuyNowPayLater } from 'pages/Quote/helpers';

import { APRIsVisible, formatAPRLabel } from './helpers';

import type { FormattedRates } from '../constants';

export interface APRsHook {
  options: SelectOption[];
  optionsHasBuyNowPayLater: boolean;
}

export default function useAPRs(rates: FormattedRates | []): APRsHook {
  const { job } = useCurrentJob();
  const { lead } = useCurrentJobLead();

  const [interest]: [string] = useWatch({
    name: [FIELD_NAMES.interest],
  });

  const hasCardPayments = useMemo(() => {
    if (!job) return false;
    return Boolean(job?.checkout_options?.includes('pay_on_card'));
  }, [job]);

  const previousInterest = usePreviousValue(interest);

  const { setValue } = useFormContext();

  const ratesToUse = useMemo(
    () => (rates.length === 0 ? {} : (rates as FormattedRates)),
    [rates],
  );

  const options = useMemo(
    () =>
      Object.keys(ratesToUse)
        .filter((value) => APRIsVisible(ratesToUse, value, job, lead))
        .map((value) => ({
          name: someRatesAreBuyNowPayLater(ratesToUse, value)
            ? 'BNPL'
            : formatAPRLabel(value),
          value,
        })),
    [ratesToUse, job, lead],
  );

  const optionsHasBuyNowPayLater = useMemo(
    () => someRatesAreBuyNowPayLater(ratesToUse),
    [ratesToUse],
  );

  useEffect(() => {
    const [rate] = ratesToUse[interest] || [];
    if (rate && previousInterest !== interest) {
      setValue(FIELD_NAMES.duration, rate.duration);
    }
  }, [ratesToUse, interest, setValue, previousInterest]);

  // Effect auto selects if only one apr
  useEffect(() => {
    if (!rates || hasCardPayments) return;
    const aprs = Object.keys(rates);
    if (aprs.length !== 1) return;
    setValue('interest', aprs[0]);
  }, [hasCardPayments, rates, setValue]);

  return {
    options,
    optionsHasBuyNowPayLater,
  };
}
